import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { lodash as _, newShortTimeStamp } from '../../../common';
import Chip from '@material-ui/core/Chip';
import List from '@material-ui/core/List';
import classNames from 'classnames';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { AutoFocusTextField } from '../../ux/Inputs';
import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from '../../icons';
import { LinkButton, OutlinedButton } from '../../ux/Buttons';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import SweetAlert from '../../feedback/SweetAlert';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Priority from '../Priority';

const targetToDraft = (resolutionTargetMins) => {
   if (resolutionTargetMins == null) return { targetValue: '', targetUnit: 'hours' };
   if (resolutionTargetMins % 1440 === 0) return { targetValue: resolutionTargetMins / 1440, targetUnit: 'days' };
   return { targetValue: resolutionTargetMins / 60, targetUnit: 'hours' };
};

const draftToTargetMins = ({ targetValue, targetUnit }) => {
   if (targetValue === '' || targetValue == null) return null;
   const value = Number(targetValue);
   if (!Number.isInteger(value) || value <= 0) return NaN;
   return value * (targetUnit === 'days' ? 1440 : 60);
};

const formatResolutionTarget = (resolutionTargetMins) => {
   if (resolutionTargetMins == null) return 'No automatic deadline';
   if (resolutionTargetMins % 1440 === 0) {
      const days = resolutionTargetMins / 1440;
      return `${days} ${days === 1 ? 'day' : 'days'} resolution target`;
   }
   const hours = resolutionTargetMins / 60;
   return `${hours} ${hours === 1 ? 'hour' : 'hours'} resolution target`;
};


const PRIORITY_DND_TYPE = 'priority-order-row';

const PriorityOrderRow = ({ priority, index, disabled, canMoveUp, canMoveDown, onDropAt, onMoveUp, onMoveDown, children }) => {
   const rowRef = useRef(null);
   const handleRef = useRef(null);
   const [anchorEl, setAnchorEl] = useState(null);

   const [{ isDragging }, drag, preview] = useDrag({
      item: { type: PRIORITY_DND_TYPE, noPriority: priority.no, fromIndex: index },
      canDrag: !disabled && !priority.editing && priority.no != null,
      collect: (monitor) => ({ isDragging: monitor.isDragging() })
   });

   const [{ isOver, canDrop, draggedFromIndex }, drop] = useDrop({
      accept: PRIORITY_DND_TYPE,
      canDrop: (item) => !disabled && item.noPriority !== priority.no,
      drop: (item, monitor) => {
         if (monitor.didDrop()) return;
         onDropAt(item.fromIndex, index);
      },
      collect: (monitor) => ({
         isOver: monitor.isOver({ shallow: true }),
         canDrop: monitor.canDrop(),
         draggedFromIndex: monitor.getItem() ? monitor.getItem().fromIndex : null
      })
   });

   preview(drop(rowRef));
   drag(handleRef);

   return (
      <div
         ref={rowRef}
         style={{
            position: 'relative',
            opacity: isDragging ? 0.55 : 1,
            borderTop: isOver && canDrop && draggedFromIndex > index ? '2px solid #1976d2' : '2px solid transparent',
            borderBottom: isOver && canDrop && draggedFromIndex < index ? '2px solid #1976d2' : '2px solid transparent'
         }}>
         <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
               ref={handleRef}
               aria-label={`Drag ${priority.name} Priority`}
               disabled={disabled || priority.editing || priority.no == null}
               style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
               <DragIndicatorIcon />
            </IconButton>
            <div style={{ flex: 1 }}>{children}</div>
            {!priority.editing && priority.no != null && (
               <>
                  <IconButton aria-label={`More actions for ${priority.name}`} disabled={disabled} onClick={(event) => setAnchorEl(event.currentTarget)}>
                     <MoreVertIcon />
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                     <MenuItem disabled={!canMoveUp || disabled} onClick={() => { setAnchorEl(null); onMoveUp(index); }}>Move up</MenuItem>
                     <MenuItem disabled={!canMoveDown || disabled} onClick={() => { setAnchorEl(null); onMoveDown(index); }}>Move down</MenuItem>
                  </Menu>
               </>
            )}
         </div>
      </div>
   );
};

const PriorityEditor = ({
   priorities = [],
   onAddRequested = (priority) => {},
   onDeleteRequested = (priority) => {},
   onUpdateRequested = (priority) => {},
   onReorderRequested = () => {},
   reorderSaving = false,
   reorderError = null
}) => {
   const [lines, setLines] = useState([]);
   const [isAdding, setIsAdding] = useState(false);
   const [priorityToDelete, setPriorityToDelete] = useState(null);
   const [hasDuplicate, setHasDuplicate] = useState(false);
   const [showAddButton, setShowAddButton] = useState(false);
   const previousOrderRef = useRef(null);

   useEffect(() => {
      const showAdd =
         !isAdding &&
         !_.some(lines, (s) => {
            return s.editing;
         });

      setShowAddButton(showAdd);
   }, [lines, isAdding]);

   useEffect(() => {
      const list = [];

      _.each(priorities, (t) => {
         const targetDraft = targetToDraft(t.resolutionTargetMins);
         list.push({
            no: t.no,
            name: t.name,
            color: t.color,
            resolutionTargetMins: t.resolutionTargetMins == null ? null : t.resolutionTargetMins,
            targetValue: targetDraft.targetValue,
            targetUnit: targetDraft.targetUnit,
            newTargetValue: targetDraft.targetValue,
            newTargetUnit: targetDraft.targetUnit,
            editing: false,
            errored: false,
            targetErrored: false,
            newName: '',
            newColor: t.color
         });
      });

      setLines(list);
      previousOrderRef.current = null;
   }, [priorities]);

   useEffect(() => {
      if (reorderError && previousOrderRef.current) {
         setLines(previousOrderRef.current);
         previousOrderRef.current = null;
      }
   }, [reorderError]);

   const checkDuplicate = (priority) => {
      const idxExisting = _.findIndex(priorities, (s) => {
         return s.name === priority.newName;
      });

      return idxExisting > -1;
   };

   const _onEditCancelled = (priority) => {
      const newLines = [...lines];

      if (priority.no != null) {
         // editing
         var s = _.find(newLines, (s) => {
            return s.no === priority.no;
         });
         if (s) {
            const targetDraft = targetToDraft(s.resolutionTargetMins);
            s.editing = false;
            s.newName = s.name;
            s.newColor = s.color;
            s.newTargetValue = targetDraft.targetValue;
            s.newTargetUnit = targetDraft.targetUnit;
            s.targetErrored = false;
         }
         setLines(newLines);
      } else {
         var idx = _.findIndex(newLines, (s) => {
            return s.no === priority.no;
         });

         newLines.splice(idx, 1);

         setLines(newLines);
         setIsAdding(false);
      }
   };

   const _onEditConfirmed = (priority) => {
      console.log('_onEditConfirmed', priority);
      const newLines = [...lines];
      const resolutionTargetMins = draftToTargetMins({
         targetValue: priority.newTargetValue,
         targetUnit: priority.newTargetUnit
      });

      if (Number.isNaN(resolutionTargetMins) || resolutionTargetMins > 5256000) {
         const invalid = _.find(newLines, (line) => line.no === priority.no);
         if (invalid) invalid.targetErrored = true;
         setLines(newLines);
         return;
      }

      let idx = -1;

      if (priority.no != null) {
         console.log('Edit confirmed');

         if (
            priority.newName === priority.name &&
            priority.newColor === priority.color &&
            resolutionTargetMins === priority.resolutionTargetMins
         ) {
            _onEditCancelled(priority);
            return;
         }

         idx = _.findIndex(newLines, (s) => {
            return s.no === priority.no;
         });

         const targetDraft = targetToDraft(resolutionTargetMins);
         let o = {
            no: priority.no,
            name: priority.newName,
            color: priority.newColor,
            resolutionTargetMins,
            targetValue: targetDraft.targetValue,
            targetUnit: targetDraft.targetUnit,
            newTargetValue: targetDraft.targetValue,
            newTargetUnit: targetDraft.targetUnit,
            newName: '',
            newColor: priority.newColor,
            targetErrored: false,
            editing: false
         };

         newLines.splice(idx, 1, o);

         setHasDuplicate(false);
         setIsAdding(false);
         setLines(newLines);

         console.log('-====');
         console.log(o);

         onUpdateRequested(o);
      } else {
         console.log('add confirmed');
         idx = _.findIndex(newLines, (s) => {
            return s.no === priority.no;
         });

         const targetDraft = targetToDraft(resolutionTargetMins);
         let o = {
            no: newShortTimeStamp(),
            name: priority.newName,
            newName: priority.newName,
            color: priority.newColor,
            newColor: priority.newColor,
            resolutionTargetMins,
            targetValue: targetDraft.targetValue,
            targetUnit: targetDraft.targetUnit,
            newTargetValue: targetDraft.targetValue,
            newTargetUnit: targetDraft.targetUnit,
            targetErrored: false,
            editing: false
         };

         newLines.splice(idx, 1, o);

         setHasDuplicate(false);
         setIsAdding(false);
         setLines(newLines);

         onAddRequested(o);
      }
   };

   const _onAddRequested = () => {
      const newLines = [...lines];
      newLines.push({
         no: null,
         name: '',
         newName: '',
         color: '#000000',
         newColor: '#000000',
         resolutionTargetMins: null,
         targetValue: '',
         targetUnit: 'hours',
         newTargetValue: '',
         newTargetUnit: 'hours',
         targetErrored: false,
         editing: true
      });
      setIsAdding(true);
      setLines(newLines);
   };

   const _onNameChanged = (priority, e) => {
      const newLines = [...lines];
      let hasDuplicateVal = hasDuplicate;

      var s = _.find(newLines, (s) => {
         return s.no === priority.no;
      });
      if (s) {
         s.newName = e.target.value;
         s.errored = checkDuplicate(s);
         hasDuplicateVal = s.errored;
      }

      setLines(newLines);
      setHasDuplicate(hasDuplicateVal);
   };

   const _onColorChange = (priority, hex) => {
      const newLines = [...lines];

      var s = _.find(newLines, (s) => {
         return s.no === priority.no;
      });

      console.log('s', s);
      if (s) {
         s.newColor = hex;
      }

      setLines(newLines);
   };

   const _onTargetValueChanged = (priority, value) => {
      const newLines = [...lines];
      const line = _.find(newLines, (item) => item.no === priority.no);
      if (line) {
         line.newTargetValue = value;
         const targetMins = draftToTargetMins({ targetValue: value, targetUnit: line.newTargetUnit });
         line.targetErrored = Number.isNaN(targetMins) || targetMins > 5256000;
      }
      setLines(newLines);
   };

   const _onTargetUnitChanged = (priority, unit) => {
      const newLines = [...lines];
      const line = _.find(newLines, (item) => item.no === priority.no);
      if (line) {
         line.newTargetUnit = unit;
         const targetMins = draftToTargetMins({ targetValue: line.newTargetValue, targetUnit: unit });
         line.targetErrored = Number.isNaN(targetMins) || targetMins > 5256000;
      }
      setLines(newLines);
   };

   const _onEditRequested = (priority) => {
      console.log('_onEditRequested!', priority);

      const newLines = [...lines];

      var s = _.find(newLines, (s) => {
         return s.no === priority.no;
      });
      if (s) {
         const targetDraft = targetToDraft(priority.resolutionTargetMins);
         s.editing = true;
         s.newName = priority.name;
         s.newColor = priority.color;
         s.newTargetValue = targetDraft.targetValue;
         s.newTargetUnit = targetDraft.targetUnit;
         s.targetErrored = false;
      }

      var idx = _.findIndex(newLines, (s) => {
         return s.no === null;
      });
      if (idx > -1) {
         newLines.splice(idx, 1);
      }

      setLines(newLines);
      setIsAdding(false);
   };

   const _onDeleteRequested = (priority) => {
      console.log('delete priority', priority);
      setPriorityToDelete(priority);
   };

   const _onDeleteCancelled = () => {
      console.log('CANCELLED!');
      setPriorityToDelete(null);
   };

   const _onDeleteConfirmed = () => {
      console.log('Confirmed!');

      const d = { ...priorityToDelete };

      setPriorityToDelete(null);

      onDeleteRequested(d);
   };

   const _reorder = (fromIndex, toIndex) => {
      if (reorderSaving || fromIndex === toIndex || toIndex < 0 || toIndex >= lines.length) return;
      const reorderedLines = [...lines];
      const [moved] = reorderedLines.splice(fromIndex, 1);
      reorderedLines.splice(toIndex, 0, moved);

      previousOrderRef.current = [...lines];
      setLines(reorderedLines);
      onReorderRequested({ orderedPriorityNos: reorderedLines.filter((line) => line.no != null).map((line) => line.no) });
   };

   const renderLines = () => {
      return lines.map((sl) => {
         const key = 'priority-list-item-' + (sl.no || 'new');

         return (
            <PriorityOrderRow
               key={key}
               priority={sl}
               index={lines.indexOf(sl)}
               disabled={reorderSaving || isAdding}
               canMoveUp={lines.indexOf(sl) > 0}
               canMoveDown={lines.indexOf(sl) < lines.length - 1}
               onDropAt={_reorder}
               onMoveUp={(index) => _reorder(index, index - 1)}
               onMoveDown={(index) => _reorder(index, index + 1)}>
               <ListItem className={classNames('priorityItem', { editing: sl.editing }, { errored: sl.errored })}>
                  <ListItemIcon>
                     {!sl.editing && <Priority color={sl.color} showBackground={false} editing={false} />}
                     {sl.editing && (
                        <Priority
                           color={sl.newColor}
                           showBackground={true}
                           editing={true}
                           onColorChange={(hex) => _onColorChange(sl, hex)}
                        />
                     )}
                  </ListItemIcon>
                  {!sl.editing && <ListItemText primary={sl.name} secondary={formatResolutionTarget(sl.resolutionTargetMins)} />}
                  {sl.editing && (
                     <div className='priorityEditFields'>
                        <AutoFocusTextField
                           id={sl.no ? 'priorityName_' + sl.no : 'priorityName_new'}
                           margin='none'
                           placeholder='Give it a name...'
                           value={sl.newName}
                           autoComplete='off'
                           onChange={(e) => _onNameChanged(sl, e)}
                           onKeyDown={(ev) => {
                              if (ev.key === 'Escape') {
                                 _onEditCancelled(sl);
                                 return;
                              }
                              if (ev.key === 'Enter') {
                                 ev.preventDefault();

                                 if (sl.newName === '' || sl.errored || sl.targetErrored) {
                                    return;
                                 }

                                 _onEditConfirmed(sl);
                              }
                           }}
                        />
                        <TextField
                           className='priorityTargetValue'
                           type='number'
                           label='Resolution target'
                           placeholder='Optional'
                           value={sl.newTargetValue}
                           error={sl.targetErrored}
                           helperText={sl.targetErrored ? 'Enter a positive whole number up to 10 years' : 'Existing Issue deadlines will not change'}
                           inputProps={{ min: 1, step: 1 }}
                           onChange={(e) => _onTargetValueChanged(sl, e.target.value)}
                        />
                        <TextField
                           className='priorityTargetUnit'
                           select
                           label='Unit'
                           value={sl.newTargetUnit}
                           SelectProps={{ native: true }}
                           onChange={(e) => _onTargetUnitChanged(sl, e.target.value)}>
                           <option value='hours'>Hours</option>
                           <option value='days'>Days</option>
                        </TextField>
                     </div>
                  )}
                  <ListItemSecondaryAction>
                     {sl.editing && (
                        <>
                           <IconButton
                              disabled={sl.newName === '' || sl.errored || sl.targetErrored}
                              edge='end'
                              aria-label='update'
                              onClick={() => _onEditConfirmed(sl)}>
                              <CheckIcon style={sl.newName !== '' && !sl.errored && !sl.targetErrored ? { color: 'green' } : {}} />
                           </IconButton>
                           <IconButton edge='end' aria-label='cancel-edit' onClick={() => _onEditCancelled(sl)}>
                              <CloseIcon />
                           </IconButton>
                        </>
                     )}
                     {!sl.editing && (
                        <>
                           <IconButton edge='end' aria-label='edit' onClick={() => _onEditRequested(sl)}>
                              <EditIcon />
                           </IconButton>
                           <IconButton edge='end' aria-label='delete' onClick={() => _onDeleteRequested(sl)}>
                              <DeleteIcon />
                           </IconButton>
                        </>
                     )}
                  </ListItemSecondaryAction>
               </ListItem>
               <Divider />
            </PriorityOrderRow>
         );
      });
   };

   const noPrioritys = priorities.length;

   return (
      <div className={'PriorityEditor'}>
         {reorderError && <Typography color='error'>{reorderError}</Typography>}
         <DndProvider backend={HTML5Backend}>
            <List>{renderLines()}</List>
         </DndProvider>
         {showAddButton && (
            <OutlinedButton className='btn-add_optype' color='primary' onClick={_onAddRequested}>
               Add a Priority
            </OutlinedButton>
         )}
         {priorityToDelete !== null && (
            <SweetAlert
               show={true}
               warning
               showCancel
               confirmBtnText={'Yes'}
               cancelBtnText={'No'}
               confirmBtnBsStyle='warning'
               cancelBtnBsStyle='default'
               title={'Delete Priority?'}
               onConfirm={_onDeleteConfirmed}
               onCancel={_onDeleteCancelled}>
               <Typography>Are you sure you want to delete the '{priorityToDelete.name}' Priority?</Typography>
            </SweetAlert>
         )}
      </div>
   );
};
export default PriorityEditor;
